import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { isPro, canAskAI, incrementAICount } from '../utils/featureGate';
import StreamingText from '../components/StreamingText';
import { useIsMobile } from '../hooks/useWindowWidth';
import EmailCapture from '../components/EmailCapture';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const GOALS = [
  { id: 'emergency',   icon: '🛡️', label: 'Build an emergency fund' },
  { id: 'debt',        icon: '💳', label: 'Pay off debt' },
  { id: 'invest',      icon: '📈', label: 'Start investing' },
  { id: 'house',       icon: '🏠', label: 'Save for a house' },
  { id: 'retirement',  icon: '🏖️', label: 'Plan for retirement' },
  { id: 'wealth',      icon: '💰', label: 'Build wealth long-term' },
];

const QUESTIONS = [
  { num: 1, icon: '🎂', color: '#38bdf8', label: 'How old are you?',                key: 'age',    type: 'number', placeholder: 'e.g. 28',   prefix: null, suffix: null, hint: 'Helps us prioritize retirement timing and investment horizon.' },
  { num: 2, icon: '💵', color: '#22c55e', label: 'Monthly take-home pay?',          key: 'income', type: 'number', placeholder: 'e.g. 3,500', prefix: '$',  suffix: null, hint: 'After-tax. Everything we recommend scales from this number.' },
  { num: 3, icon: '💳', color: '#ef4444', label: 'How much debt do you carry?',     key: 'debt',   type: 'number', placeholder: 'e.g. 8,000', prefix: '$',  suffix: null, hint: 'Credit cards, loans, car payments — anything you owe.' },
  { num: 4, icon: '🏦', color: '#a78bfa', label: 'What do you have saved & invested?', key: 'savings', type: 'number', placeholder: 'e.g. 5,000', prefix: '$', suffix: null, hint: 'Savings, 401(k), IRA, brokerage — your total financial baseline.' },
];

function GamePlan() {
  const isMobile = useIsMobile();
  const s = {
    page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: isMobile ? '32px 16px 48px' : '60px 24px 80px', position: 'relative', overflow: 'hidden' },
    glow: { position: 'absolute', top: '-120px', left: '50%', transform: 'translateX(-50%)', width: '900px', height: '700px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.1) 0%, rgba(168,139,250,0.06) 40%, transparent 70%)', pointerEvents: 'none' },
    inner: { maxWidth: '640px', margin: '0 auto', position: 'relative', zIndex: 1 },

    // Header
    header: { textAlign: 'center', marginBottom: '52px' },
    eyebrow: { display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', padding: '5px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', marginBottom: '20px', letterSpacing: '0.3px' },
    title: { fontSize: 'clamp(40px,7vw,62px)', fontWeight: '900', margin: '0 0 16px', letterSpacing: '-2px', lineHeight: 1.05, background: 'linear-gradient(135deg, #f8fafc 30%, #94a3b8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
    sub: { color: '#64748b', fontSize: '16px', lineHeight: 1.7, maxWidth: '460px', margin: '0 auto' },

    // Question cards
    questions: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' },
    qCard: { background: '#1e293b', borderRadius: '16px', border: '1px solid #334155', padding: '20px 22px', display: 'flex', gap: '16px', alignItems: 'flex-start', transition: 'border-color 0.2s' },
    qLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0, paddingTop: '2px' },
    qBadge: { fontSize: '11px', fontWeight: '800', padding: '3px 8px', borderRadius: '6px', letterSpacing: '0.5px' },
    qIcon: { fontSize: '22px' },
    qBody: { flex: 1 },
    qLabel: { fontSize: '16px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' },
    qHint: { fontSize: '12px', color: '#475569', marginBottom: '12px', lineHeight: 1.5 },

    // Inputs
    inputWrap: { position: 'relative' },
    prefix: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#475569', zIndex: 1, fontSize: '15px', pointerEvents: 'none' },
    suffix: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none', fontSize: '14px' },
    input: { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f8fafc', padding: '11px 14px', fontSize: '15px', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s' },

    // APR
    aprToggle: { background: 'none', border: 'none', color: '#475569', fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' },
    aprRow: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
    aprCost: { fontSize: '12px', color: '#64748b' },

    // Goals
    goalsSection: { background: '#1e293b', borderRadius: '16px', border: '1px solid #334155', padding: '22px', marginBottom: '28px' },
    goalsHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
    goalsBadge: { fontSize: '14px', fontWeight: '700', color: '#f1f5f9' },
    goalsNote: { fontSize: '12px', color: '#475569' },
    goalGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '8px' },
    goalBtn: { background: '#0f172a', border: '1px solid #334155', color: '#64748b', padding: '11px 14px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', transition: 'all 0.15s', position: 'relative' },
    goalBtnActive: { background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.4)', color: '#f1f5f9', fontWeight: '600' },
    goalBtnIcon: { fontSize: '16px', flexShrink: 0 },
    goalCheck: { marginLeft: 'auto', color: '#38bdf8', fontWeight: '800', fontSize: '13px' },

    // CTA
    ctaWrap: { textAlign: 'center' },
    btn: { display: 'inline-flex', alignItems: 'center', gap: '10px', background: 'linear-gradient(135deg, #38bdf8, #818cf8)', color: '#0f172a', border: 'none', padding: '17px 40px', borderRadius: '14px', fontWeight: '900', fontSize: '17px', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 8px 32px rgba(56,189,248,0.25)', letterSpacing: '-0.3px' },
    btnDisabled: { opacity: 0.35, cursor: 'not-allowed', boxShadow: 'none' },
    btnIcon: { fontSize: '18px' },
    btnArrow: { fontSize: '18px', marginLeft: '2px' },
    validNote: { color: '#475569', fontSize: '13px', marginTop: '10px', marginBottom: '0' },
    disclaimer: { color: '#334155', fontSize: '12px', marginTop: '14px', marginBottom: 0 },

    // Plan output
    planContainer: {},
    planHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
    planBadge: { background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '700' },
    resetBtn: { background: 'transparent', border: '1px solid #334155', color: '#64748b', padding: '6px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' },
    planCard: { background: '#1e293b', borderRadius: '16px', padding: '32px', border: '1px solid rgba(56,189,248,0.2)', overflow: 'hidden', position: 'relative' },
    blurWrap: { position: 'relative', marginTop: '4px' },
    blurredPreview: { color: '#e2e8f0', lineHeight: 1.85, fontSize: '15px', whiteSpace: 'pre-wrap', filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.6 },
    blurFade: { position: 'absolute', top: 0, left: 0, right: 0, height: '80px', background: 'linear-gradient(to bottom, transparent, #1e293b)', pointerEvents: 'none' },
    proWall: { background: 'linear-gradient(135deg, rgba(56,189,248,0.06), rgba(99,102,241,0.06))', border: '2px solid rgba(56,189,248,0.25)', borderRadius: '14px', padding: '28px', textAlign: 'center', marginTop: '8px' },
    proWallIcon: { fontSize: '32px', marginBottom: '10px' },
    proWallTitle: { fontSize: '20px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 10px', letterSpacing: '-0.3px' },
    proWallDesc: { color: '#94a3b8', fontSize: '14px', lineHeight: 1.65, margin: '0 0 20px', maxWidth: '420px', display: 'block', marginLeft: 'auto', marginRight: 'auto' },
    proWallBtn: { display: 'inline-block', background: '#38bdf8', color: '#0f172a', padding: '14px 32px', borderRadius: '10px', fontWeight: '800', fontSize: '16px', textDecoration: 'none' },
    proWallNote: { color: '#334155', fontSize: '12px', marginTop: '12px', marginBottom: 0 },
    nextSteps: { marginTop: '24px', background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: '16px', padding: '24px', textAlign: 'center' },
    nextTitle: { color: '#64748b', fontSize: '14px', marginBottom: '16px', marginTop: 0 },
    nextBtns: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
    actionBtn: { background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '10px 18px', borderRadius: '10px', textDecoration: 'none', fontSize: '14px', fontWeight: '600' },
  };

  const [form, setForm] = useState({ age: '', income: '', debt: '', debtApr: '', savings: '', goals: [] });
  const [showApr, setShowApr] = useState(false);
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const pro = isPro();

  const toggleGoal = (id) => {
    setForm(f => ({
      ...f,
      goals: f.goals.includes(id) ? f.goals.filter(x => x !== id) : [...f.goals, id],
    }));
  };

  const isValid = form.age && form.income && form.goals.length > 0;

  const generate = async () => {
    if (!isValid) return;
    if (!canAskAI()) {
      setStarted(true);
      setPlan("You've used your 5 free AI uses for today. Upgrade to Pro for more.");
      setLoading(false);
      return;
    }
    incrementAICount();
    setStarted(true);
    setLoading(true);
    setPlan('');
    try {
      const response = await fetch(`${API}/api/gameplan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          age: form.age,
          income: form.income,
          debt: form.debt || '0',
          debtApr: form.debtApr || '',
          savings: form.savings || '0',
          investments: '0',
          crypto: '0',
          goals: form.goals.map(id => GOALS.find(g => g.id === id)?.label).join(', '),
        }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      setLoading(false);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setPlan(prev => prev + decoder.decode(value, { stream: true }));
      }
    } catch {
      setPlan('Sorry, the AI is unavailable right now. Please try again.');
      setLoading(false);
    }
  };

  const reset = () => {
    setStarted(false);
    setPlan('');
    setShowApr(false);
    setForm({ age: '', income: '', debt: '', debtApr: '', savings: '', goals: [] });
  };

  const monthlyInterest = form.debt && form.debtApr
    ? Math.round(parseFloat(form.debt) * parseFloat(form.debtApr) / 100 / 12)
    : null;

  return (
    <div style={s.page}>
      <SEO
        title="Build Your Free Money Game Plan — Personalized Financial Plan"
        description="Answer 4 questions and get a step-by-step financial plan built around your actual numbers. Free, instant, no jargon."
        path="/gameplan"
        keywords="personal financial plan, money game plan, financial planning tool, free financial advice, budget plan"
      />
      <div style={s.glow} />
      <div style={s.inner}>

        {/* ── Header ── */}
        <div style={s.header}>
          <div style={s.eyebrow}>🎯 Personalized for you · Free</div>
          <h1 style={s.title}>Your Money<br />Game Plan</h1>
          <p style={s.sub}>4 questions. 30 seconds. A step-by-step financial plan built around your actual numbers — not generic advice.</p>
        </div>

        {!started ? (
          <>
            {/* ── Question Cards ── */}
            <div style={s.questions}>
              {QUESTIONS.map(q => (
                <div key={q.key} style={s.qCard}>
                  <div style={s.qLeft}>
                    <div style={{ ...s.qBadge, background: q.color + '18', color: q.color, border: `1px solid ${q.color}33` }}>
                      Q{q.num}
                    </div>
                    <span style={s.qIcon}>{q.icon}</span>
                  </div>
                  <div style={s.qBody}>
                    <div style={s.qLabel}>{q.label}</div>
                    <div style={s.qHint}>{q.hint}</div>
                    <div style={s.inputWrap}>
                      {q.prefix && <span style={s.prefix}>{q.prefix}</span>}
                      <input
                        style={{ ...s.input, paddingLeft: q.prefix ? '28px' : '14px', borderColor: form[q.key] ? q.color + '55' : '#334155', boxShadow: form[q.key] ? `0 0 0 1px ${q.color}22` : 'none' }}
                        type={q.type}
                        placeholder={q.placeholder}
                        value={form[q.key]}
                        onChange={e => setForm(f => ({ ...f, [q.key]: e.target.value }))}
                      />
                    </div>

                    {/* APR expansion for debt card */}
                    {q.key === 'debt' && form.debt && (
                      <div style={{ marginTop: '10px' }}>
                        {!showApr ? (
                          <button style={s.aprToggle} onClick={() => setShowApr(true)}>
                            + Add interest rate (APR) for a more accurate plan
                          </button>
                        ) : (
                          <div style={s.aprRow}>
                            <div style={{ position: 'relative', width: '130px' }}>
                              <input
                                style={{ ...s.input, paddingLeft: '14px', paddingRight: '28px', fontSize: '14px' }}
                                type="number"
                                placeholder="e.g. 24"
                                value={form.debtApr}
                                onChange={e => setForm(f => ({ ...f, debtApr: e.target.value }))}
                              />
                              <span style={s.suffix}>%</span>
                            </div>
                            {monthlyInterest !== null && (
                              <div style={s.aprCost}>
                                ≈ <strong style={{ color: '#ef4444' }}>${monthlyInterest.toLocaleString()}/mo</strong> in pure interest
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Goals ── */}
            <div style={s.goalsSection}>
              <div style={s.goalsHeader}>
                <div style={s.goalsBadge}>🏁 What are your goals?</div>
                <div style={s.goalsNote}>Pick everything that applies</div>
              </div>
              <div style={s.goalGrid}>
                {GOALS.map(g => {
                  const active = form.goals.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      style={{ ...s.goalBtn, ...(active ? s.goalBtnActive : {}) }}
                      onClick={() => toggleGoal(g.id)}
                    >
                      <span style={s.goalBtnIcon}>{g.icon}</span>
                      <span>{g.label}</span>
                      {active && <span style={s.goalCheck}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── CTA ── */}
            <div style={s.ctaWrap}>
              <button
                style={{ ...s.btn, ...(!isValid ? s.btnDisabled : {}) }}
                onClick={generate}
                disabled={!isValid}
              >
                <span style={s.btnIcon}>🎯</span>
                Build My Game Plan
                <span style={s.btnArrow}>→</span>
              </button>
              {!isValid && (
                <p style={s.validNote}>
                  {!form.age || !form.income ? 'Fill in your age and income to continue' : 'Pick at least one goal'}
                </p>
              )}
              <p style={s.disclaimer}>🔒 Financial education only — not investment advice. Free forever.</p>
            </div>
          </>
        ) : (
          /* ── Plan Output ── */
          <div style={s.planContainer}>
            <div style={s.planHeader}>
              <span style={s.planBadge}>🎯 Your Personal Money Game Plan</span>
              {!loading && plan && (
                <button style={s.resetBtn} onClick={reset}>← Start Over</button>
              )}
            </div>

            {(plan || loading) && (() => {
              const FREE_LIMIT = 480;
              const isLocked = !pro && !loading && plan.length >= FREE_LIMIT;
              const visibleText = (!pro && plan.length > FREE_LIMIT) ? plan.slice(0, FREE_LIMIT) : plan;

              return (
                <div style={s.planCard}>
                  <StreamingText
                    text={visibleText}
                    loading={loading}
                    style={{ color: '#e2e8f0', lineHeight: 1.85, fontSize: '15px', whiteSpace: 'pre-wrap', display: 'block' }}
                  />
                  {isLocked && (
                    <div style={s.blurWrap}>
                      <div style={s.blurredPreview}>{plan.slice(FREE_LIMIT, FREE_LIMIT + 300)}</div>
                      <div style={s.blurFade} />
                      <div style={s.proWall}>
                        <div style={s.proWallIcon}>🔒</div>
                        <h3 style={s.proWallTitle}>Your full plan is ready</h3>
                        <p style={s.proWallDesc}>
                          Upgrade to <strong>KnowFi Pro</strong> to see your complete step-by-step plan — including investment projections, crypto guidance, and your personalized return estimates.
                        </p>
                        <Link to="/signup?plan=pro" style={s.proWallBtn}>Unlock with Pro — $12/mo →</Link>
                        <p style={s.proWallNote}>Cancel anytime · All features unlocked</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {!loading && plan && pro && (
              <div style={s.nextSteps}>
                <p style={s.nextTitle}>Ready to take action?</p>
                <div style={s.nextBtns}>
                  <Link to="/calculators" style={s.actionBtn}>📊 Run the Numbers</Link>
                  <Link to="/ask" style={s.actionBtn}>🤖 Ask a Question</Link>
                  <Link to="/courses" style={s.actionBtn}>🎓 Take a Course</Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


      {/* Email capture */}
      <div style={{ marginTop: '48px', marginBottom: '24px' }}>
        <EmailCapture variant="card" source="gameplan" title="Get smarter about money — free." subtitle="The KnowFi Daily Brief: plain English market news every morning." />
      </div>

export default GamePlan;
